import { createContext, useContext, useMemo, useReducer } from 'react'
import { useLocation } from 'react-router-dom'

import type { PropsWithChildren, ReactElement } from 'react'
import type {
  IActionReducer,
  ICompareCohortsContext,
  ICompareCohortsState,
  IStateReducer,
  TFilters,
} from '~/types/compare-cohorts-context'

import { DEFAULT_DAI_MULTIPLE_VALUES, DEFAULT_DAI_SINGLE_VALUE, DEFAULT_DATE_RANGE, LOCAL_STORAGE } from '~/const'

const DEFAULT_KPI = 'installation_date'
const DEFAULT_COUNTRY = 'US'

const getReducerAction = (action: IActionReducer, state: IStateReducer) => {
  switch (action.type) {
    case 'filters':
      // NOTE: reset abtest_key values
      if (action.payload.breakdown && action.payload.breakdown?.[0] !== 'abtest') {
        action.payload = {
          ...action.payload,
          abtestKey: [],
        }
      }

      // NOTE: reset placement_key values
      if (action.payload.breakdown && action.payload.breakdown?.[0] !== 'dsv3') {
        action.payload = {
          ...action.payload,
          navigationGraphId: [],
          placementKey: [],
        }
      }

      // NOTE: reset dai values
      if (action.payload.breakdown && action.payload.breakdown?.[0] !== 'dai') {
        action.payload = {
          ...action.payload,
          dai: state.filters.kpi?.[0] === 'dai' ? DEFAULT_DAI_MULTIPLE_VALUES : DEFAULT_DAI_SINGLE_VALUE,
        }
      } else if (state.filters.breakdown?.[0] !== 'dai' && action.payload.breakdown?.[0] === 'dai') {
        action.payload = {
          ...action.payload,
          dai: DEFAULT_DAI_MULTIPLE_VALUES,
        }
      }

      const newFilters: TFilters = { ...state.filters, ...action.payload[action.cohort] }
      const newComputedFilters = computeFilters(newFilters)
      const newComputedFiltersDate = computeFiltersDate(newFilters)

      return {
        ...state,
        computedFilters: newComputedFilters,
        computedFiltersDate: newComputedFiltersDate,
        filters: newFilters,
      }

    case 'resetFilters':
      return {
        ...state,
        computedFilters: defaultState[action.cohort].computedFilters,
        computedFiltersDate: defaultState[action.cohort].computedFiltersDate,
        filters: defaultState[action.cohort].filters,
      }
    default:
      return state
  }
}

const reducer = (state: ICompareCohortsState, action: IActionReducer): ICompareCohortsState => {
  switch (action.cohort) {
    case 'cohort1':
      return { ...state, cohort1: getReducerAction(action, state.cohort1) }
    case 'cohort2':
      return { ...state, cohort2: getReducerAction(action, state.cohort2) }
    default:
      return state
  }
}

export const computeFilters = (
  filters: TFilters
): {
  key: string
  value: string[]
}[] => {
  return [
    { key: 'abtest_key', value: filters.abtestKey || [] },
    { key: 'breakdown', value: filters.breakdown || [] },
    { key: 'dai', value: filters.dai || DEFAULT_DAI_SINGLE_VALUE },
    { key: 'device_category', value: filters.deviceCategory || [] },
    { key: 'device_type', value: filters.deviceType || [] },
    { key: 'installation_app_version', value: filters.installationAppVersion || [] },
    { key: 'installation_country', value: filters.installationCountry || [DEFAULT_COUNTRY] },
    { key: 'kpi', value: filters.kpi || [DEFAULT_KPI] },
    { key: 'navigation_graph_id', value: filters.navigationGraphId || [] },
    { key: 'placement_key', value: filters.placementKey || [] },
    { key: 'ua_source', value: filters.uaSource || [] },
    { key: 'user_type', value: filters.userType || [] },
  ]
}

export const computeFiltersDate = (
  filters: TFilters
): {
  range: {
    end: string
    start: string
  }
} => {
  const { endDate, startDate } = filters
  const dateRange = {
    end: endDate ? endDate[0] : DEFAULT_DATE_RANGE.end,
    start: startDate ? startDate[0] : DEFAULT_DATE_RANGE.start,
  }

  return {
    range: dateRange,
  }
}

////////////////////////////////////////////////////
// INITIAL STATE
////////////////////////////////////////////////////

const initState = {
  computedFilters: computeFilters({
    abtestKey: [],
    dai: DEFAULT_DAI_SINGLE_VALUE,
    deviceCategory: [],
    deviceType: [],
    installationAppVersion: [],
    installationCountry: [DEFAULT_COUNTRY],
    kpi: [DEFAULT_KPI],
    navigationGraphId: [],
    placementKey: [],
    uaSource: [],
    userType: [],
  }),
  computedFiltersDate: {
    range: DEFAULT_DATE_RANGE,
  },
  displayedFilters: {
    datePicker: true,
    dropdownBreakdown: false,
    dropdownDai: true,
    dropdownDeviceCategory: true,
    dropdownInstallationAppVersion: true,
    dropdownInstallationCountry: true,
    dropdownUaSource: true,
    dropdownUserType: true,
  },
  filters: {
    abtestKey: [],
    breakdown: [],
    dai: DEFAULT_DAI_SINGLE_VALUE,
    deviceCategory: [],
    deviceType: [],
    endDate: [DEFAULT_DATE_RANGE.end],
    installationAppVersion: [],
    installationCountry: [DEFAULT_COUNTRY],
    kpi: [DEFAULT_KPI],
    navigationGraphId: [],
    placementKey: [],
    startDate: [DEFAULT_DATE_RANGE.start],
    uaSource: [],
    userType: [],
  },
}

export const defaultState: ICompareCohortsState = {
  cohort1: initState,
  cohort2: initState,
}

export const CompareCohortsContext = createContext<ICompareCohortsContext>({
  dispatch: (): ICompareCohortsState => defaultState,
  state: defaultState,
})

const useCompareCohortsFilters = () => {
  const { key, search } = useLocation()

  let filters: Record<string, Record<string, string[]>> = {}

  // initial location https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey
  // NOTE: if this is a shared link, and direct access, take the query params as source of truth and erase the locale storage
  // otherwise/then take the locale storage as source of truth
  if (key === 'default') {
    const query = new URLSearchParams(search)

    query.forEach((value, key) => {
      if (key !== 'app') {
        filters[key] = JSON.parse(value)
      }
    })

    localStorage.setItem(LOCAL_STORAGE.COMPARE_COHORTS_FILTERS, JSON.stringify(filters))
  } else {
    filters = JSON.parse(localStorage.getItem(LOCAL_STORAGE.COMPARE_COHORTS_FILTERS) || '{}')
  }

  const newFilters: Record<string, Record<string, string[]>> = {}

  for (const key in filters) {
    const filterValues = filters[key as string]
    newFilters[key as string] = filterValues
  }

  return newFilters
}

const getInitialState = (filters: Record<string, Record<string, string[]>>) => {
  let stateToReturn = defaultState

  if ('cohort1' in filters) {
    stateToReturn = {
      ...stateToReturn,
      cohort1: {
        ...initState,
        computedFilters: computeFilters(filters.cohort1),
        computedFiltersDate: {
          ...initState.computedFiltersDate,
          ...computeFiltersDate(filters.cohort1),
        },
        filters: {
          ...initState.filters,
          ...filters.cohort1,
        },
      },
    }
  }

  if ('cohort2' in filters) {
    stateToReturn = {
      ...stateToReturn,
      cohort2: {
        ...initState,
        computedFilters: computeFilters(filters.cohort2),
        computedFiltersDate: {
          ...initState.computedFiltersDate,
          ...computeFiltersDate(filters.cohort2),
        },
        filters: {
          ...initState.filters,
          ...filters.cohort2,
        },
      },
    }
  }

  return stateToReturn
}

const CompareCohortsContextProvider = ({ children }: PropsWithChildren): ReactElement => {
  /** Local state */

  const filters = useCompareCohortsFilters()

  const initialState = getInitialState(filters)

  const [state, dispatch] = useReducer(reducer, initialState)

  const contextCompareCohorts = useMemo(() => ({ dispatch, state }), [state])

  return <CompareCohortsContext.Provider value={contextCompareCohorts}>{children}</CompareCohortsContext.Provider>
}

// context consumer hook
const useCompareCohortsContext = (): ICompareCohortsContext => {
  const context = useContext(CompareCohortsContext)

  if (context === undefined) {
    throw new Error('useCompareCohortsContext was used outside of its Provider')
  }

  return context
}

export { CompareCohortsContextProvider, useCompareCohortsContext }
